import L from 'leaflet';
import { api } from '../api/client';
import type { DeviceSummary, OverviewResponse, TrailPoint, UserSummary } from '../api/types';
import { MARKER_STALE_RED_MINUTES, MARKER_STALE_YELLOW_MINUTES, OVERVIEW_REFRESH_MS } from '../config';
import { BASEMAP_ATTRIBUTION, BASEMAP_TILE_OPTIONS, basemapTileUrl } from '../lib/basemap';
import { batteryColor, colorForId, initialsFromName, stalenessMeta } from '../lib/format';
import { escapeHtml } from '../lib/html';
import { navigate } from '../router';
import { relativeTime, staleness } from '../lib/time';
import { largestFreeRect, type Rect } from '../lib/visibleRect';

const TRAIL_COLOR = '#4f9cf0';
const IMPRECISE_COLOR = '#e6a23c';

interface Entry {
  user: UserSummary;
  device: DeviceSummary;
}

function meta(device: DeviceSummary) {
  return stalenessMeta(staleness(device.last_seen_at, MARKER_STALE_YELLOW_MINUTES, MARKER_STALE_RED_MINUTES));
}

export function mount(root: HTMLElement): () => void {
  root.innerHTML = `
    <div class="map-page">
      <div id="ft-map" class="map-container"></div>
      <div class="map-rail" id="map-rail">
        <div class="map-rail-header">
          <div class="rail-title">People &amp; devices</div>
          <button type="button" class="rail-toggle-all" id="rail-toggle-all">Hide all</button>
        </div>
        <div class="map-rail-body" id="map-rail-body"></div>
        <div class="map-rail-footer">
          <span><span class="status-dot" style="background:var(--fresh)"></span>Live</span>
          <span><span class="status-dot" style="background:var(--stale)"></span>Delayed</span>
          <span><span class="status-dot" style="background:var(--offline)"></span>Offline</span>
        </div>
      </div>
      <div class="map-detail" id="map-detail" hidden></div>
      <div class="map-dock" id="map-dock">
        <div class="dock-row">
          <span class="dock-label">Trail</span>
          <div class="segmented" id="trail-segmented">
            <button type="button" data-hours="0">Off</button>
            <button type="button" data-hours="6">6h</button>
            <button type="button" data-hours="24">24h</button>
            <button type="button" data-hours="72">72h</button>
          </div>
          <span class="dock-scrub-time" id="dock-scrub-time"></span>
        </div>
        <div id="dock-scrub-row"></div>
        <div id="dock-hint" class="dock-hint" hidden>
          Select a device to scrub its history and see direction of travel over time.
        </div>
      </div>
    </div>
  `;

  const mapEl = root.querySelector<HTMLDivElement>('#ft-map')!;
  const railEl = root.querySelector<HTMLElement>('#map-rail')!;
  const railBody = root.querySelector<HTMLElement>('#map-rail-body')!;
  const railToggleAll = root.querySelector<HTMLButtonElement>('#rail-toggle-all')!;
  const detailEl = root.querySelector<HTMLElement>('#map-detail')!;
  const dockEl = root.querySelector<HTMLElement>('#map-dock')!;
  const trailSegmented = root.querySelector<HTMLElement>('#trail-segmented')!;
  const scrubTimeEl = root.querySelector<HTMLElement>('#dock-scrub-time')!;
  const scrubRowEl = root.querySelector<HTMLElement>('#dock-scrub-row')!;
  const dockHintEl = root.querySelector<HTMLElement>('#dock-hint')!;

  const focusParams = new URLSearchParams(window.location.search);
  const focusLat = focusParams.has('lat') ? Number(focusParams.get('lat')) : null;
  const focusLng = focusParams.has('lng') ? Number(focusParams.get('lng')) : null;
  const hasFocus = focusLat !== null && focusLng !== null && !Number.isNaN(focusLat) && !Number.isNaN(focusLng);

  // preferCanvas: a dense trail can be hundreds of points; drawing them as
  // individual SVG DOM elements (Leaflet's default) is what made rendering
  // the trail slow enough to block the UI. Canvas draws them as pixels on
  // one element instead, which is dramatically cheaper for many points.
  // tolerance: trail dots are only ~3px, too small to hit exactly, so extend
  // the canvas hit-test area around every path.
  const map = L.map(mapEl, { zoomControl: false, preferCanvas: true, renderer: L.canvas({ tolerance: 6 }) }).setView(
    hasFocus ? [focusLat as number, focusLng as number] : [40.7128, -74.006],
    hasFocus ? 17 : 12,
  );
  let tileLayer = L.tileLayer(basemapTileUrl(document.documentElement.getAttribute('data-theme') ?? 'dark'), {
    attribution: BASEMAP_ATTRIBUTION,
    ...BASEMAP_TILE_OPTIONS,
  }).addTo(map);
  L.control.zoom({ position: 'bottomright' }).addTo(map);

  function onThemeChange(e: Event): void {
    const theme = (e as CustomEvent<string>).detail;
    map.removeLayer(tileLayer);
    tileLayer = L.tileLayer(basemapTileUrl(theme), {
      attribution: BASEMAP_ATTRIBUTION,
      ...BASEMAP_TILE_OPTIONS,
    }).addTo(map);
  }
  window.addEventListener('ft-theme-change', onThemeChange);

  const markerLayer = L.layerGroup().addTo(map);
  const trailLayer = L.layerGroup().addTo(map);

  // The rail, dock, and detail cards float over the map container, so what
  // Leaflet considers "centered" can land underneath them - on phones the
  // rail alone covers the bottom 44%. Find the largest card-free rectangle
  // at call time (the layout shifts between breakpoints and selection state)
  // and express it as insets from the map edges. Considering the cards
  // together matters: the trail dock floats just above the bottom sheet,
  // almost mid-map, so no per-card edge assignment describes it.
  function overlayInsets(): { top: number; bottom: number; left: number; right: number } {
    const m = mapEl.getBoundingClientRect();
    const cards: Rect[] = [];
    for (const el of [railEl, dockEl, detailEl]) {
      if (el.hidden || el.offsetParent === null) continue;
      cards.push(el.getBoundingClientRect());
    }
    const free = largestFreeRect(m, cards);
    return {
      top: free.top - m.top,
      bottom: m.bottom - free.bottom,
      left: free.left - m.left,
      right: m.right - free.right,
    };
  }

  const FIT_PAD = 40;

  function fitVisible(bounds: L.LatLngBounds): void {
    const inset = overlayInsets();
    const size = map.getSize();
    // Padding at or beyond the map size makes Leaflet's zoom math meaningless
    // (this is how v1.6.2 ended up at maxZoom on phones); if the free region
    // is implausibly small, a whole-container fit beats a nonsense one.
    if (size.x - inset.left - inset.right < 3 * FIT_PAD || size.y - inset.top - inset.bottom < 3 * FIT_PAD) {
      map.fitBounds(bounds, { padding: [FIT_PAD, FIT_PAD], maxZoom: 15 });
      return;
    }
    map.fitBounds(bounds, {
      paddingTopLeft: L.point(inset.left + FIT_PAD, inset.top + FIT_PAD),
      paddingBottomRight: L.point(inset.right + FIT_PAD, inset.bottom + FIT_PAD),
      maxZoom: 15,
    });
  }

  // setView, but aimed at the center of the un-obscured part of the map.
  function centerVisible(latlng: L.LatLngExpression, zoom: number, animate = true): void {
    const inset = overlayInsets();
    const size = map.getSize();
    if (size.x - inset.left - inset.right < 3 * FIT_PAD || size.y - inset.top - inset.bottom < 3 * FIT_PAD) {
      map.setView(latlng, zoom, { animate });
      return;
    }
    const shift = L.point((inset.left - inset.right) / 2, (inset.top - inset.bottom) / 2);
    map.setView(map.unproject(map.project(L.latLng(latlng), zoom).subtract(shift), zoom), zoom, { animate });
  }

  // A focused coordinate (e.g. "Open in full map" from a check-in log) drops a
  // distinct pin so the location is actually visible rather than just being
  // re-centered under the live device markers.
  if (hasFocus) {
    const focusColor = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#4f9cf0';
    L.circleMarker([focusLat as number, focusLng as number], {
      radius: 8,
      color: '#fff',
      weight: 2,
      fillColor: focusColor,
      fillOpacity: 1,
    })
      .addTo(map)
      .bindPopup('Check-in location')
      .openPopup();
    centerVisible([focusLat as number, focusLng as number], 17, false);
  }
  const markers = new Map<string, L.Marker>();
  const markerHtmlCache = new Map<string, string>();
  let scrubMarker: L.CircleMarker | null = null;

  let disposed = false;
  let latestOverview: OverviewResponse = { users: [] };
  let hidden = new Set<string>();
  let selectedId: string | null = null;
  let trailHours = 0;
  let trailPoints: TrailPoint[] = [];
  let trailRequestId = 0;
  let scrubIndex: number | null = null;
  let didInitialFit = false;

  function activeEntries(): Entry[] {
    const out: Entry[] = [];
    for (const user of latestOverview.users) {
      for (const device of user.devices) {
        if (device.status !== 'active') continue;
        out.push({ user, device });
      }
    }
    return out;
  }

  function visibleEntries(): Entry[] {
    return activeEntries().filter((e) => !hidden.has(e.device.device_id));
  }

  function findEntry(deviceId: string | null): Entry | null {
    if (!deviceId) return null;
    return activeEntries().find((e) => e.device.device_id === deviceId) ?? null;
  }

  function selectDevice(deviceId: string | null): void {
    selectedId = deviceId;
    scrubIndex = null;
    trailPoints = [];
    loadTrailIfNeeded();
    renderAll();
  }

  function toggleVisibility(deviceId: string): void {
    if (hidden.has(deviceId)) hidden.delete(deviceId);
    else hidden.add(deviceId);
    renderRail();
    renderMarkers();
  }

  function toggleUserVisibility(userId: string): void {
    const deviceIds = activeEntries()
      .filter((e) => e.user.user_id === userId)
      .map((e) => e.device.device_id);
    if (deviceIds.length === 0) return;
    const anyVisible = deviceIds.some((id) => !hidden.has(id));
    for (const id of deviceIds) {
      if (anyVisible) hidden.add(id);
      else hidden.delete(id);
    }
    renderRail();
    renderMarkers();
  }

  function toggleAll(): void {
    const all = activeEntries();
    const anyVisible = all.some((e) => !hidden.has(e.device.device_id));
    hidden = anyVisible ? new Set(all.map((e) => e.device.device_id)) : new Set();
    renderRail();
    renderMarkers();
  }

  function setTrailHours(hours: number): void {
    trailHours = hours;
    scrubIndex = null;
    loadTrailIfNeeded();
    renderDock();
  }

  function loadTrailIfNeeded(): void {
    const requestId = ++trailRequestId;
    // Any open trail-point popup describes the trail being replaced.
    map.closePopup();
    if (!selectedId || trailHours === 0) {
      trailPoints = [];
      renderDock();
      renderTrail();
      return;
    }
    api
      .trail(selectedId, trailHours)
      .then((res) => {
        if (disposed || requestId !== trailRequestId) return;
        trailPoints = res.points;
        // Paint the now-taller dock (with the scrub slider) before doing the
        // heavier trail-layer rebuild below - a dense trail (hundreds of
        // points over several hours) can take a while to redraw on the map,
        // and running both in the same task blocks the dock's own repaint
        // until that finishes, so the slider appears to not show up until
        // something else (e.g. a scroll) forces a repaint.
        renderDock();
        // A single requestAnimationFrame still runs before the browser has
        // actually painted the renderDock() change above - both would land
        // in the same paint. Nesting a second one forces a real paint to
        // happen in between, so the dock shows up right away instead of
        // waiting on renderTrail() below.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (disposed || requestId !== trailRequestId) return;
            renderTrail();
          });
        });
      })
      .catch((err) => {
        console.error('failed to load trail', err);
      });
  }

  function markerHtml(entry: Entry, selected: boolean): string {
    const st = meta(entry.device);
    const isFresh = staleness(entry.device.last_seen_at, MARKER_STALE_YELLOW_MINUTES, MARKER_STALE_RED_MINUTES) === 'fresh';
    const color = colorForId(entry.user.user_id);
    const initials = initialsFromName(entry.user.display_name);
    return `<div style="position:relative;width:38px;height:38px;display:grid;place-items:center">
      ${isFresh ? `<div style="position:absolute;inset:0;border-radius:50%;background:${st.color};opacity:.35;animation:ftpulse 2s ease-out infinite"></div>` : ''}
      <div style="position:relative;width:26px;height:26px;border-radius:50%;background:${color};border:3px solid ${st.color};box-shadow:0 2px 6px rgba(0,0,0,.55);display:grid;place-items:center;color:#fff;font:700 10px 'Hanken Grotesk',sans-serif">${initials}</div>
      ${selected ? '<div style="position:absolute;inset:-4px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 2px rgba(0,0,0,.3)"></div>' : ''}
    </div>`;
  }

  function renderMarkers(): void {
    const entries = visibleEntries();
    const seen = new Set<string>();

    for (const entry of entries) {
      if (entry.device.last_lat === null || entry.device.last_lng === null) continue;
      const deviceId = entry.device.device_id;
      seen.add(deviceId);
      const latlng: L.LatLngExpression = [entry.device.last_lat, entry.device.last_lng];
      const html = markerHtml(entry, deviceId === selectedId);

      let marker = markers.get(deviceId);
      if (!marker) {
        marker = L.marker(latlng, {
          icon: L.divIcon({ className: '', html, iconSize: [38, 38], iconAnchor: [19, 19] }),
          zIndexOffset: deviceId === selectedId ? 1000 : 0,
        });
        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          selectDevice(deviceId);
        });
        marker.addTo(markerLayer);
        markers.set(deviceId, marker);
        markerHtmlCache.set(deviceId, html);
      } else {
        marker.setLatLng(latlng);
        marker.setZIndexOffset(deviceId === selectedId ? 1000 : 0);
        if (markerHtmlCache.get(deviceId) !== html) {
          marker.setIcon(L.divIcon({ className: '', html, iconSize: [38, 38], iconAnchor: [19, 19] }));
          markerHtmlCache.set(deviceId, html);
        }
      }
    }

    for (const [deviceId, marker] of markers) {
      if (seen.has(deviceId)) continue;
      markerLayer.removeLayer(marker);
      markers.delete(deviceId);
      markerHtmlCache.delete(deviceId);
    }

    if (!didInitialFit && !hasFocus && entries.length > 0) {
      didInitialFit = true;
      const bounds = L.latLngBounds(
        entries
          .filter((e) => e.device.last_lat !== null && e.device.last_lng !== null)
          .map((e) => [e.device.last_lat as number, e.device.last_lng as number]),
      );
      fitVisible(bounds);
    }
  }

  function renderTrail(): void {
    trailLayer.clearLayers();
    scrubMarker = null;
    if (trailPoints.length < 2) return;
    const entry = findEntry(selectedId);
    if (!entry) return;

    const latlngs = trailPoints.map((p) => [p.lat, p.lng] as [number, number]);
    L.polyline(latlngs, { color: TRAIL_COLOR, weight: 3, opacity: 0.5, dashArray: '1 7', lineCap: 'round' }).addTo(trailLayer);

    trailPoints.forEach((p, i) => {
      const t = i / (trailPoints.length - 1);
      const dotColor = p.imprecise ? IMPRECISE_COLOR : TRAIL_COLOR;
      // bubblingMouseEvents: false - a dot click must not reach the map's own
      // click handler, which deselects the device and clears the whole trail.
      const dot = L.circleMarker([p.lat, p.lng], {
        radius: 2.5 + t * 2,
        color: dotColor,
        weight: 0,
        fillColor: dotColor,
        fillOpacity: 0.25 + t * 0.6,
        bubblingMouseEvents: false,
      }).addTo(trailLayer);
      dot.on('click', () => openTrailPointPopup(i));
    });

    const color = colorForId(entry.user.user_id);
    scrubMarker = L.circleMarker([0, 0], {
      radius: 9,
      color: '#fff',
      weight: 2,
      fillColor: color,
      fillOpacity: 1,
      bubblingMouseEvents: false,
    }).addTo(trailLayer);
    scrubMarker.on('click', () => {
      openTrailPointPopup(scrubIndex === null ? trailPoints.length - 1 : Math.min(scrubIndex, trailPoints.length - 1));
    });
    updateScrubMarker();
  }

  function openTrailPointPopup(idx: number): void {
    const p = trailPoints[idx];
    if (!p) return;

    // Sync the scrub slider to the clicked dot so the dock, highlight marker
    // and popup all describe the same point.
    scrubIndex = idx === trailPoints.length - 1 ? null : idx;
    renderDock();
    updateScrubMarker();

    const battery = p.battery !== null ? `${p.battery}%` : '—';
    const accuracy = p.accuracy !== null ? `±${Math.round(p.accuracy)} m` : '—';
    L.popup({ offset: [0, -6] })
      .setLatLng([p.lat, p.lng])
      .setContent(
        `<div class="trail-popup">
          <div class="tp-time">${new Date(p.timestamp).toLocaleString()}</div>
          <div class="tp-rel">${relativeTime(p.timestamp)} &middot; point ${idx + 1} of ${trailPoints.length}</div>
          ${p.imprecise ? `<div class="tp-imprecise">Approximate location</div>` : ''}
          <div class="tp-row"><span class="label">Battery</span><span style="color:${batteryColor(p.battery)}">${battery}</span></div>
          <div class="tp-row"><span class="label">Accuracy</span><span class="mono">${accuracy}</span></div>
          <div class="tp-row"><span class="label">Coordinates</span><span class="mono">${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}</span></div>
        </div>`,
      )
      .openOn(map);
  }

  // Moves only the highlighted scrub-position dot. Called on every drag tick,
  // so it must stay cheap - rebuilding the whole trail layer (renderTrail)
  // here is what made continuous dragging on the scrub slider freeze up,
  // since it couldn't keep up with a flood of 'input' events.
  function updateScrubMarker(): void {
    if (!scrubMarker || trailPoints.length < 2) return;
    const idx = scrubIndex === null ? trailPoints.length - 1 : Math.min(scrubIndex, trailPoints.length - 1);
    const sp = trailPoints[idx];
    scrubMarker.setLatLng([sp.lat, sp.lng]);
  }

  function centerSelected(): void {
    const entry = findEntry(selectedId);
    if (entry && entry.device.last_lat !== null && entry.device.last_lng !== null) {
      centerVisible([entry.device.last_lat, entry.device.last_lng], 16);
    }
  }

  function renderRail(): void {
    const entries = activeEntries();
    const byUser = new Map<string, Entry[]>();
    for (const e of entries) {
      const list = byUser.get(e.user.user_id) ?? [];
      list.push(e);
      byUser.set(e.user.user_id, list);
    }

    const anyVisible = entries.some((e) => !hidden.has(e.device.device_id));
    railToggleAll.textContent = anyVisible ? 'Hide all' : 'Show all';

    railBody.innerHTML = '';
    for (const user of latestOverview.users) {
      const devices = byUser.get(user.user_id) ?? [];
      const memberDiv = document.createElement('div');
      memberDiv.className = 'rail-member';

      const color = colorForId(user.user_id);
      const hasDevice = devices.length > 0;
      memberDiv.innerHTML = `
        <div class="rail-member-row${hasDevice ? '' : ' no-devices'}">
          <span class="status-dot" style="background:${hasDevice ? 'var(--fresh)' : 'var(--faint)'}"></span>
          <span class="rail-avatar" style="background:${color}">${escapeHtml(initialsFromName(user.display_name))}</span>
          <span class="rail-member-name">${escapeHtml(user.display_name)}</span>
        </div>
      `;
      if (hasDevice) {
        memberDiv.querySelector<HTMLElement>('.rail-member-row')!.addEventListener('click', () => {
          toggleUserVisibility(user.user_id);
        });
      }

      for (const entry of devices) {
        const st = meta(entry.device);
        const isHidden = hidden.has(entry.device.device_id);
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'rail-device-row' + (entry.device.device_id === selectedId ? ' selected' : '');
        row.innerHTML = `
          <span class="sc-dot" style="background:${st.color};box-shadow:0 0 0 3px ${st.glow}"></span>
          <span class="rail-device-info">
            <span class="rail-device-name">${escapeHtml(entry.device.device_name)}</span>
            <span class="rail-device-status" style="color:${st.color}">${st.label} &middot; ${relativeTime(entry.device.last_seen_at)}</span>
          </span>
          <span class="rail-device-batt" style="color:${batteryColor(entry.device.last_battery)}">${entry.device.last_battery !== null ? entry.device.last_battery + '%' : '—'}</span>
          <span class="vis-toggle" style="background:${isHidden ? 'var(--surface-3)' : 'var(--accent)'}"><span class="knob" style="left:${isHidden ? '2px' : '12px'}"></span></span>
        `;
        row.addEventListener('click', (e) => {
          const toggle = (e.target as HTMLElement).closest('.vis-toggle');
          if (toggle) {
            e.stopPropagation();
            toggleVisibility(entry.device.device_id);
            return;
          }
          selectDevice(entry.device.device_id);
        });
        memberDiv.appendChild(row);
      }

      railBody.appendChild(memberDiv);
    }
  }

  function renderDetail(): void {
    const entry = findEntry(selectedId);
    if (!entry) {
      detailEl.hidden = true;
      detailEl.innerHTML = '';
      return;
    }
    detailEl.hidden = false;
    const { user, device } = entry;
    const st = meta(device);
    const color = colorForId(user.user_id);
    const coords =
      device.last_lat !== null && device.last_lng !== null ? `${device.last_lat.toFixed(5)}, ${device.last_lng.toFixed(5)}` : 'unknown';
    detailEl.innerHTML = `
      <div class="map-detail-head">
        <span class="map-detail-avatar" style="background:${color};box-shadow:0 0 0 2px ${st.color}">${escapeHtml(initialsFromName(user.display_name))}</span>
        <div style="flex:1;min-width:0">
          <div class="map-detail-name">${escapeHtml(user.display_name)}</div>
          <div class="map-detail-sub">${escapeHtml(device.device_name)} &middot; ${escapeHtml(device.model || 'unknown')}</div>
        </div>
        <button type="button" class="map-detail-close" id="detail-close">&times;</button>
      </div>
      <div class="map-detail-status" style="background:${st.glow}">
        <span class="status-dot" style="background:${st.color}"></span>
        <span class="status-text" style="color:${st.color}">${st.label}</span>
        <span class="status-time">${relativeTime(device.last_seen_at)}</span>
      </div>
      <div class="map-detail-grid">
        <div class="map-detail-cell"><div class="label">Battery</div><div class="value" style="color:${batteryColor(device.last_battery)}">${device.last_battery !== null ? device.last_battery + '%' : '—'}</div></div>
        <div class="map-detail-cell"><div class="label">Accuracy</div><div class="value mono">${device.last_accuracy !== null ? `±${Math.round(device.last_accuracy)} m` : '—'}</div></div>
        <div class="map-detail-cell span2"><div class="label">Coordinates</div><div class="value mono">${coords}</div></div>
      </div>
      <div class="map-detail-actions">
        <button type="button" class="btn-primary-outline" id="detail-center">Center on map</button>
        <button type="button" class="btn-secondary" id="detail-log">Check-in log</button>
      </div>
    `;
    detailEl.querySelector<HTMLButtonElement>('#detail-close')!.addEventListener('click', () => selectDevice(null));
    detailEl.querySelector<HTMLButtonElement>('#detail-center')!.addEventListener('click', centerSelected);
    detailEl.querySelector<HTMLButtonElement>('#detail-log')!.addEventListener('click', () => {
      navigate(`/people/devices/${encodeURIComponent(device.device_id)}/checkins`);
    });
  }

  function updateScrubTime(): void {
    const idx = scrubIndex === null ? trailPoints.length - 1 : Math.min(scrubIndex, trailPoints.length - 1);
    const isLive = scrubIndex === null || idx === trailPoints.length - 1;
    scrubTimeEl.textContent = isLive ? `Now · ${relativeTime(trailPoints[idx].timestamp)}` : relativeTime(trailPoints[idx].timestamp);
  }

  function renderDock(): void {
    trailSegmented.querySelectorAll<HTMLButtonElement>('button[data-hours]').forEach((btn) => {
      const h = Number(btn.dataset.hours);
      btn.classList.toggle('active', h === trailHours);
      btn.classList.toggle('accent', h === trailHours && h !== 0);
    });

    const scrubActive = selectedId !== null && trailHours > 0 && trailPoints.length > 1;
    const scrubHint = selectedId === null && trailHours > 0;
    dockHintEl.hidden = !scrubHint;
    dockEl.classList.toggle('has-selection', selectedId !== null);

    if (!scrubActive) {
      scrubTimeEl.textContent = '';
      scrubRowEl.innerHTML = '';
      return;
    }

    const idx = scrubIndex === null ? trailPoints.length - 1 : Math.min(scrubIndex, trailPoints.length - 1);
    updateScrubTime();

    scrubRowEl.innerHTML = `
      <div class="dock-scrub-row">
        <button type="button" class="scrub-btn" id="scrub-prev">&lsaquo;</button>
        <div class="scrub-slider-col">
          <input type="range" class="ft-scrub" id="scrub-range" min="0" max="${trailPoints.length - 1}" value="${idx}">
          <div class="scrub-range-labels"><span>&minus;${trailHours}h</span><span>now</span></div>
        </div>
        <button type="button" class="scrub-btn" id="scrub-next">&rsaquo;</button>
        <button type="button" class="scrub-live-btn" id="scrub-live">Live</button>
      </div>
    `;
    scrubRowEl.querySelector<HTMLButtonElement>('#scrub-prev')!.addEventListener('click', () => scrubStep(-1));
    scrubRowEl.querySelector<HTMLButtonElement>('#scrub-next')!.addEventListener('click', () => scrubStep(1));
    scrubRowEl.querySelector<HTMLButtonElement>('#scrub-live')!.addEventListener('click', () => {
      scrubIndex = null;
      map.closePopup();
      renderDock();
      updateScrubMarker();
    });
    scrubRowEl.querySelector<HTMLInputElement>('#scrub-range')!.addEventListener('input', (e) => {
      // Only update the time label and move the scrub marker here, not the
      // DOM structure or the full trail layer - rebuilding scrubRowEl.innerHTML
      // mid-drag would replace this very input element and break the
      // browser's drag gesture, and rebuilding the whole trail (renderTrail)
      // is too slow to keep up with a flood of 'input' events while dragging.
      scrubIndex = Number((e.target as HTMLInputElement).value);
      // A trail-point popup left open here would describe a different point
      // than the one the slider now shows.
      map.closePopup();
      updateScrubTime();
      updateScrubMarker();
    });
  }

  function scrubStep(dir: number): void {
    if (trailPoints.length === 0) return;
    const cur = scrubIndex === null ? trailPoints.length - 1 : scrubIndex;
    scrubIndex = Math.max(0, Math.min(trailPoints.length - 1, cur + dir));
    map.closePopup();
    renderDock();
    updateScrubMarker();
  }

  function renderAll(): void {
    renderRail();
    renderMarkers();
    renderDetail();
    renderDock();
    renderTrail();
  }

  railToggleAll.addEventListener('click', toggleAll);
  trailSegmented.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-hours]');
    if (!btn) return;
    setTrailHours(Number(btn.dataset.hours));
  });
  map.on('click', () => selectDevice(null));

  async function refresh(): Promise<void> {
    try {
      latestOverview = await api.overview();
      if (disposed) return;
      renderAll();
    } catch (err) {
      console.error('failed to load overview', err);
    }
  }

  refresh();
  renderDock();
  const intervalId = window.setInterval(refresh, OVERVIEW_REFRESH_MS);

  return () => {
    disposed = true;
    window.clearInterval(intervalId);
    window.removeEventListener('ft-theme-change', onThemeChange);
    map.remove();
  };
}
